import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Undo2, AlertTriangle } from "lucide-react";

interface ReverseCommitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReversed: () => void;
}

interface CommitGroup {
  campaign_name: string;
  mailed_date: string;
  count: number;
  created_at: string;
}

interface PreviewRecord {
  id: string;
  mailing_record_id: string;
  campaign_name: string;
  mailed_date: string;
  mailing_round: number;
  first_name: string | null;
  last_name: string | null;
  business_name: string | null;
  city: string | null;
  state: string | null;
  county: string | null;
}

export function ReverseCommitDialog({ open, onOpenChange, onReversed }: ReverseCommitDialogProps) {
  const [loading, setLoading] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [recentCommits, setRecentCommits] = useState<CommitGroup[]>([]);
  const [selectedCommit, setSelectedCommit] = useState<CommitGroup | null>(null);
  const [previewRecords, setPreviewRecords] = useState<PreviewRecord[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [totalPreview, setTotalPreview] = useState(0);

  useEffect(() => {
    if (open) {
      fetchRecentCommits();
      setSelectedCommit(null);
      setPreviewRecords([]);
    }
  }, [open]);

  const fetchRecentCommits = async () => {
    setLoading(true);
    try {
      // Get distinct campaign+date combos from recent history, ordered by created_at desc
      const { data, error } = await supabase
        .from("mailing_history")
        .select("campaign_name, mailed_date, created_at")
        .not("campaign_name", "is", null)
        .order("created_at", { ascending: false })
        .limit(1000);

      if (error) throw error;

      // Group by campaign_name + mailed_date
      const groups = new Map<string, CommitGroup>();
      for (const row of data || []) {
        const key = `${row.campaign_name}|${row.mailed_date}`;
        if (!groups.has(key)) {
          groups.set(key, {
            campaign_name: row.campaign_name!,
            mailed_date: row.mailed_date || "unknown",
            count: 0,
            created_at: row.created_at,
          });
        }
        groups.get(key)!.count++;
      }

      // Take only the most recent 10 distinct commits
      const sorted = Array.from(groups.values()).sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setRecentCommits(sorted.slice(0, 10));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load recent commits");
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async (commit: CommitGroup) => {
    setSelectedCommit(commit);
    setPreviewLoading(true);
    try {
      // Get all history IDs for this commit
      let allRecords: PreviewRecord[] = [];
      let from = 0;
      const pageSize = 500;

      while (true) {
        const { data, error } = await supabase
          .from("mailing_history")
          .select("id, mailing_record_id, campaign_name, mailed_date, mailing_round")
          .eq("campaign_name", commit.campaign_name)
          .eq("mailed_date", commit.mailed_date)
          .range(from, from + pageSize - 1);

        if (error) throw error;
        if (!data || data.length === 0) break;

        // Fetch names for preview
        const recordIds = data.map(d => d.mailing_record_id);
        const { data: names } = await supabase
          .from("mailing_records")
          .select("id, first_name, last_name, business_name, city, state, county")
          .in("id", recordIds);

        const nameMap = new Map((names || []).map(n => [n.id, n]));

        for (const row of data) {
          const nameInfo = nameMap.get(row.mailing_record_id);
          allRecords.push({
            ...row,
            first_name: nameInfo?.first_name || null,
            last_name: nameInfo?.last_name || null,
            business_name: nameInfo?.business_name || null,
            city: nameInfo?.city || null,
            state: nameInfo?.state || null,
            county: nameInfo?.county || null,
          });
        }

        if (data.length < pageSize) break;
        from += pageSize;
      }

      setTotalPreview(allRecords.length);
      setPreviewRecords(allRecords.slice(0, 200)); // Show first 200 for performance
    } catch (err) {
      console.error(err);
      toast.error("Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleReverse = async () => {
    if (!selectedCommit) return;

    setReversing(true);
    try {
      // 1. Get all mailing_history entries for this commit
      let historyIds: { id: string; mailing_record_id: string; mailing_round: number }[] = [];
      let from = 0;
      const pageSize = 500;

      while (true) {
        const { data, error } = await supabase
          .from("mailing_history")
          .select("id, mailing_record_id, mailing_round")
          .eq("campaign_name", selectedCommit.campaign_name)
          .eq("mailed_date", selectedCommit.mailed_date)
          .range(from, from + pageSize - 1);

        if (error) throw error;
        if (!data || data.length === 0) break;
        historyIds.push(...data);
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (historyIds.length === 0) {
        toast.error("No records found to reverse");
        setReversing(false);
        return;
      }

      // 2. Delete mailing_history entries in batches
      const batchSize = 100;
      for (let i = 0; i < historyIds.length; i += batchSize) {
        const batch = historyIds.slice(i, i + batchSize);
        const ids = batch.map(b => b.id);
        const { error } = await supabase
          .from("mailing_history")
          .delete()
          .in("id", ids);
        if (error) console.error("Delete batch error:", error);
      }

      // 3. Decrement times_mailed and recalculate last_mailed_date for each affected record
      const uniqueRecordIds = [...new Set(historyIds.map(h => h.mailing_record_id))];

      for (let i = 0; i < uniqueRecordIds.length; i += batchSize) {
        const batch = uniqueRecordIds.slice(i, i + batchSize);

        for (const recordId of batch) {
          // Get current times_mailed
          const { data: rec } = await supabase
            .from("mailing_records")
            .select("times_mailed")
            .eq("id", recordId)
            .single();

          const newTimesMailed = Math.max(0, (rec?.times_mailed || 1) - 1);

          // Find the latest remaining mailing_history entry for this record
          const { data: latestHistory } = await supabase
            .from("mailing_history")
            .select("mailed_date")
            .eq("mailing_record_id", recordId)
            .not("mailed_date", "is", null)
            .order("mailed_date", { ascending: false })
            .limit(1);

          const newLastMailedDate = latestHistory?.[0]?.mailed_date || null;

          await supabase
            .from("mailing_records")
            .update({
              times_mailed: newTimesMailed,
              last_mailed_date: newLastMailedDate,
            })
            .eq("id", recordId);
        }
      }

      toast.success(`✅ Reversed "${selectedCommit.campaign_name}" — ${historyIds.length} history entries removed, ${uniqueRecordIds.length} records updated`);
      onReversed();
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error("Reverse failed");
    } finally {
      setReversing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="h-5 w-5" />
            Reverse Commit to Mail
          </DialogTitle>
        </DialogHeader>

        {!selectedCommit ? (
          <div className="space-y-3 flex-1 overflow-auto">
            <p className="text-sm text-muted-foreground">
              Select a recent commit to review and reverse. This will delete the mailing history entries
              and decrement times_mailed for each affected record.
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : recentCommits.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No recent commits found</p>
            ) : (
              <div className="space-y-2">
                {recentCommits.map((commit, i) => (
                  <button
                    key={i}
                    onClick={() => loadPreview(commit)}
                    className="w-full text-left p-3 rounded-lg border hover:bg-accent transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{commit.campaign_name}</span>
                        <span className="text-muted-foreground ml-2 text-sm">
                          — mailed {commit.mailed_date}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{commit.count} records</Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(commit.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center gap-3 mb-3">
              <Button variant="ghost" size="sm" onClick={() => setSelectedCommit(null)}>
                ← Back
              </Button>
              <div>
                <span className="font-medium">{selectedCommit.campaign_name}</span>
                <span className="text-muted-foreground ml-2 text-sm">
                  — {selectedCommit.mailed_date}
                </span>
              </div>
              <Badge variant="secondary">{totalPreview} records</Badge>
            </div>

            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 mb-3">
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
              <span className="text-sm text-destructive">
                Reversing will delete all {totalPreview} mailing history entries and decrement times_mailed for each record.
              </span>
            </div>

            {previewLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ScrollArea className="flex-1 border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>County</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Round</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRecords.map((rec) => (
                      <TableRow key={rec.id}>
                        <TableCell className="font-medium">
                          {rec.business_name || `${rec.first_name || ""} ${rec.last_name || ""}`.trim() || "—"}
                        </TableCell>
                        <TableCell>{rec.county || "—"}</TableCell>
                        <TableCell>{rec.city || "—"}</TableCell>
                        <TableCell>{rec.state || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline">Round {rec.mailing_round}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {totalPreview > 200 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-3">
                          Showing 200 of {totalPreview} records
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={reversing}>
            Cancel
          </Button>
          {selectedCommit && (
            <Button
              variant="destructive"
              onClick={handleReverse}
              disabled={reversing || previewLoading}
            >
              {reversing ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Reversing...</>
              ) : (
                <><Undo2 className="h-4 w-4 mr-1" /> Reverse {totalPreview} Records</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
